import { useLocalStorageState } from '@side/pantry';
import { type ReactNode, useCallback, useMemo } from 'react';
import {
  useClearAgentsAndTeamsOnViewAsSwitch,
  useViewReportAs,
} from '../../helpers';
import type { PerformanceReportFiltersFormValues } from '../types';
import {
  PERFORMANCE_REPORT_CONTEXT_DEFAULTS,
  PerformanceReportContext,
  type PerformanceReportContextType,
} from './PerformanceReportContext';

interface Props {
  children: ReactNode;
  previousViewReportAs?: string;
}

const defaultFilters = PERFORMANCE_REPORT_CONTEXT_DEFAULTS.filters;

function normalizeFilters(
  stored: PerformanceReportFiltersFormValues | undefined,
): PerformanceReportFiltersFormValues {
  return {
    ...defaultFilters,
    ...stored,
    reportingPeriod: stored?.reportingPeriod ?? defaultFilters.reportingPeriod,
    agents: stored?.agents ?? defaultFilters.agents,
    teams: stored?.teams ?? defaultFilters.teams,
    status: stored?.status ?? defaultFilters.status,
    transactionType: stored?.transactionType ?? defaultFilters.transactionType,
    leadSources: stored?.leadSources ?? defaultFilters.leadSources,
  };
}

export const PerformanceReportProvider = ({
  children,
  previousViewReportAs,
}: Props) => {
  const viewReportAs = useViewReportAs();

  const [storedFilters, setStoredFilters] =
    useLocalStorageState<PerformanceReportFiltersFormValues>(
      `performanceReportFilters-${viewReportAs}`,
      defaultFilters,
    );

  useClearAgentsAndTeamsOnViewAsSwitch(
    previousViewReportAs,
    viewReportAs,
    setStoredFilters,
  );

  const filters = useMemo(
    () => normalizeFilters(storedFilters),
    [storedFilters],
  );

  const updateContext = useCallback(
    (newContext: Partial<PerformanceReportContextType>) => {
      if ('filters' in newContext) {
        setStoredFilters(normalizeFilters(newContext.filters));
      }
    },
    [setStoredFilters],
  );

  const contextValue = useMemo(
    () => ({ filters, updateContext }),
    [filters, updateContext],
  );

  return (
    <PerformanceReportContext.Provider value={contextValue}>
      {children}
    </PerformanceReportContext.Provider>
  );
};
